/**
 * Hooks + sub-components extracted from VoiceInterview so the main file
 * is just composition. Encapsulates browser-API quirks (MediaRecorder,
 * speechSynthesis, ElevenLabs proxy) behind small, single-purpose units.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const REC_MIME_PREFERRED = "audio/webm;codecs=opus";

function pickMime() {
  return MediaRecorder.isTypeSupported(REC_MIME_PREFERRED)
    ? REC_MIME_PREFERRED
    : "audio/webm";
}

/** Returns true when this browser can record audio at all. */
export function useRecordingSupport() {
  return (
    typeof window !== "undefined" &&
    "MediaRecorder" in window &&
    !!navigator?.mediaDevices?.getUserMedia
  );
}

/**
 * Plays ``text`` via the backend ElevenLabs proxy (preferred) or falls back
 * to the browser's built-in speech synthesiser.
 */
export function useSpeaker() {
  return useCallback(async (text) => {
    if (!text || typeof window === "undefined") return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/voice/tts`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ text }),
        },
      );
      if (res.ok) {
        const blob = await res.blob();
        new Audio(URL.createObjectURL(blob)).play();
        return;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[voice] ElevenLabs unavailable:", err?.message || err);
    }
    try {
      const synth = window.speechSynthesis;
      if (!synth) return;
      synth.cancel();
      const utter = new SpeechSynthesisUtterance(text);
      synth.speak(utter);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[voice] TTS failed:", err?.message || err);
    }
  }, []);
}

/** MediaRecorder lifecycle: start / stop / cleanup, and yields a Blob. */
export function useRecorder({ onBlob }) {
  const [recording, setRecording] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const cleanup = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const recorder = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        cleanup();
        const blob = new Blob(chunksRef.current, { type: mime });
        chunksRef.current = [];
        if (blob.size < 1500) {
          toast.error(
            "That was too short to transcribe — hold the mic a little longer.",
          );
          return;
        }
        await onBlob?.(blob);
      };
      mediaRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch (err) {
      toast.error(
        "Microphone access was blocked. Allow it in your browser address bar to use the voice agent.",
      );
      // eslint-disable-next-line no-console
      console.warn("[voice] getUserMedia failed:", err);
    }
  }, [cleanup, onBlob]);

  const stop = useCallback(() => {
    try {
      mediaRef.current?.stop();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[voice] stop failed:", err);
    }
    setRecording(false);
  }, []);

  // Ensure we always release the mic when the host component unmounts.
  useEffect(() => cleanup, [cleanup]);

  return { recording, start, stop };
}

/** POST the recorded audio blob → transcribe endpoint. */
export async function transcribe(blob, context) {
  const fd = new FormData();
  fd.append("audio", blob, "recording.webm");
  if (context) fd.append("context", context);
  try {
    const { data } = await api.post("/voice/transcribe", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  } catch (err) {
    toast.error(
      formatApiErrorDetail(err?.response?.data?.detail) ||
        "Couldn't transcribe — try again in a moment.",
    );
    return null;
  }
}
